import { canPan, canStartPan, isPanModifier, passedThreshold } from '@/utils/dragScroll.js';

/**
 * `v-drag-scroll="selector"` — hold Alt (⌥), press and drag a grid to move it,
 * both ways, the way a finger moves it on a phone.
 *
 * The directive sits on the list's container and pans the box that `selector`
 * names inside it (the element itself when no selector is given), so the box
 * may be re-rendered by the table without the directive losing it. Mouse only:
 * touch already scrolls natively. The decisions live in utils/dragScroll.js;
 * the shortcut is listed in docs/raccourcis-clavier.md.
 *
 * While the pointer is over the grid with Alt held, the box carries
 * `is-drag-ready` (an open hand); while it is being dragged, `is-drag-scrolling`.
 */
class DragScroller {
    constructor(el, selector) {
        this.el = el;
        this.selector = selector;
        this.hovering = false;
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseEnter = () => (this.hovering = true);
        this.onMouseLeave = () => {
            this.hovering = false;
            this.markReady(false);
        };
        this.onKey = event => this.markReady(this.hovering && isPanModifier(event));
        this.onMouseMove = event => this.markReady(isPanModifier(event));

        el.addEventListener('mousedown', this.onMouseDown);
        el.addEventListener('mouseenter', this.onMouseEnter);
        el.addEventListener('mouseleave', this.onMouseLeave);
        el.addEventListener('mousemove', this.onMouseMove);
        document.addEventListener('keydown', this.onKey);
        document.addEventListener('keyup', this.onKey);
        window.addEventListener('blur', this.onMouseLeave);
    }

    box() {
        return this.selector ? this.el.querySelector(this.selector) : this.el;
    }

    markReady(ready) {
        const box = this.box();
        if (box) {
            box.classList.toggle('is-drag-ready', ready && canPan(box));
        }
    }

    onMouseDown(event) {
        const box = this.box();
        if (!box || !box.contains(event.target) || !canStartPan(event) || !canPan(box)) {
            return;
        }

        // A pan and a text selection cannot share one gesture; with Alt held
        // the press belongs to the pan.
        event.preventDefault();

        const startX = event.clientX;
        const startY = event.clientY;
        const startLeft = box.scrollLeft;
        const startTop = box.scrollTop;
        let panning = false;

        const onMove = moveEvent => {
            const dx = moveEvent.clientX - startX;
            const dy = moveEvent.clientY - startY;

            if (!panning) {
                if (!passedThreshold(dx, dy)) {
                    return;
                }
                panning = true;
                box.classList.add('is-drag-scrolling');
            }

            box.scrollLeft = startLeft - dx;
            box.scrollTop = startTop - dy;
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);

            if (!panning) {
                return;
            }
            box.classList.remove('is-drag-scrolling');

            // The release still fires a click on the row under the pointer;
            // after a pan it must not open that record.
            const swallow = clickEvent => {
                clickEvent.stopPropagation();
                clickEvent.preventDefault();
            };
            window.addEventListener('click', swallow, { capture: true, once: true });
            setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    destroy() {
        this.el.removeEventListener('mousedown', this.onMouseDown);
        this.el.removeEventListener('mouseenter', this.onMouseEnter);
        this.el.removeEventListener('mouseleave', this.onMouseLeave);
        this.el.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('keydown', this.onKey);
        document.removeEventListener('keyup', this.onKey);
        window.removeEventListener('blur', this.onMouseLeave);
    }
}

export const dragScroll = {
    mounted(el, binding) {
        el.__dragScroller = new DragScroller(el, binding.value);
    },
    unmounted(el) {
        el.__dragScroller?.destroy();
        delete el.__dragScroller;
    }
};
